import CaseEvidenceNotesModal from "@/components/cases/case-evidence-notes-modal";
import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { useModal } from "@/components/core/modals/use-modal.ts";
import { ScrollArea } from "@/components/core/scroller";
import Tooltip from "@/components/core/tooltip";
import { EvidenceFileViewer } from "@/components/document-viewer/evidence-file-viewer";
import { FileViewer } from "@/components/document-viewer/file-viewer";
import { userPermissions } from "@/constants/user-permissions";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { HasPermission, useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { DndContext, type DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	IconAsteriskSimple,
	IconChevronDown,
	IconDots,
	IconDragDrop,
	IconFile,
	IconGripVertical,
	IconMessageDots,
	IconMinus,
	IconPlus,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import moment from "moment";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { type UseFormReturn, useFieldArray, useForm, useWatch } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import { v4 as uuidv4 } from "uuid";
import { caseEvidenceListTsnQueryOptions, useEvidenceMutation } from "../api/queries/evidence-query";
import { caseEvidenceNoteQueryOptions } from "../api/services/evidence-notes-query";
import type { CaseActivity } from "../types/activities";
import type { CaseEvidence } from "../types/evidence";
import CaseEvidenceAddCategoryModal from "./case-evidence-add-category-modal";
import CaseEvidenceEditModal from "./case-evidence-edit-modal";
import CaseEvidenceUpdateAdmissionStatusModal from "./case-evidence-update-admission-status-modal";
import CaseEvidenceUpdateMarkingStatusModal from "./case-evidence-update-marking-status-modal";
import CaseTestimonialEvidenceFileUploadModal from "./case-testimonial-evidence-file-upload-modal";

export type Schema = {
	name: string;
	label: string;
	className?: string;
	placeholder?: string;
	type?: string;
};

export type AcceptedFile = {
	file: File | null;
	preview: string | null;
	id?: string | null;
	name?: string | null;
	_id: string;
	parent_id?: string | null;
	_lft?: number | null;
	_rgt?: number | null;
	_depth: number;
	type: string | null;
	lastModified: number;
	size: number;
	[key: string]: any;
	files?: AcceptedFile[];
	stenography: File | null;
	affidavit: File | null;
};

type BaseProps = {
	data?: any[];
	acceptedDataTypes?: string[];
	title?: string;
	schema: Schema[];
	submit?: boolean;
	buttonLabel?: string;
	dropzoneDescription?: string;
	removeFromList?: boolean;
	onRowDelete?: (row: EvidenceListAndIndex) => void;
};

type EvidenceListProps = BaseProps & {
	onEditMode?: (isEdit: boolean) => void;
	onSubmit?: (files: AcceptedFile[]) => void;
	onAdded?: (files: AcceptedFile[]) => void;
	metadata: Record<string, string | number>;
	caseData: CaseRecord;
	className?: string;
	handleAmendDelete?: (value: CaseEvidence, evidence_type: string) => void;
	handleUpdateAffidavit?: (value: CaseEvidence) => void;
	handleUpdateExhibit?: (value: CaseEvidence) => void;
	deletedData?: CaseEvidence[];
	displayOnly: boolean;
};

export type EvidenceListAndIndex = AcceptedFile & { index: number };

const mapDataToAcceptedFiles = (data: any[], schema: Schema[]): AcceptedFile[] => {
	return data.map((item) => {
		const id = item.id ? String(item.id) : null;
		const base: AcceptedFile = {
			file: item.file ?? null,
			affidavit: item.affidavit ?? null,
			preview: item.preview || "",
			parent_id: item.parent_id || null,
			id,
			_id: item._id ?? `${Date.now()}-${Math.random()}`,
			name: item.name || item.file?.file_name,
			type: item.type ?? item.file?.mime_type,
			lastModified: item.created_at ? new Date(item.created_at).getTime() : Date.now(),
			size: item.size ?? item.file?.size,
			status: item.status,
			marking_status: item.marking_status,
			admission_status: item.admission_status,
			updated_marking_status_at: item.updated_marking_status_at,
			updated_admission_status_at: item.updated_admission_status_at,
			expunged_at: item.expunged_at,
			created_at: item.created_at,
		};

		schema.map((field) => {
			base[field.name] = item[field.name] !== undefined ? item[field.name] : "";
		});

		return base;
	});
};

function EvidenceItem({
	data,
	caseData,
	onEdit,
	setIsEdit,
	file,
	removeFromList,
	metadata,
	isDragging,
	form,
	index,
	schema,
	files, // these are the child files for this file (filtered list)
	allFiles, // the full flat array so we can look up the true index for each item
	handleAmendDelete,
	deletedData,
	handleAddSubmarking,
	displayOnly,
}: {
	data?: any[];
	caseData: CaseRecord;
	metadata: Record<string, string | number>;
	file: AcceptedFile;
	index: number;
	schema: Schema[];
	form: UseFormReturn<{ files: AcceptedFile[] }>;
	isDragging?: boolean;
	files?: AcceptedFile[];
	allFiles: AcceptedFile[];
	removeFromList?: boolean;
	onEdit: boolean;
	setIsEdit: (value: React.SetStateAction<boolean>) => void;
	handleAmendDelete?: (value: CaseEvidence, evidence_type: string) => void;
	deletedData?: CaseEvidence[];
	handleAddSubmarking?: (value: boolean) => void;
	displayOnly?: boolean;
}) {
	const modal = useModal();
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
		id: file._id,
	});
	const style = { transform: CSS.Transform.toString(transform), transition };
	const [showChild, setShowChild] = useState(true);
	const [isEditing, setIsEditing] = useState(onEdit || false);
	const toggleShowChild = () => setShowChild((prev) => !prev);
	const { append, remove } = useFieldArray({
		control: form.control,
		name: "files",
	});

	useEffect(() => {
		if (onEdit === true) {
			setIsEditing(true);
		}
		if (isEditing === true) {
			setIsEdit(onEdit);
		}
	}, [setIsEditing, setIsEdit, isEditing, onEdit]);

	const handleClickStenography = async (attachment) => {
		modal.openModal({
			title: "Default Modal",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <FileViewer attachment={attachment} />,
		});
	};
	const handleClick = (item: AcceptedFile) => {
		const isValidFile = item.file?.original_url;
		const attachment = isValidFile
			? item.file
			: {
					name: item.name,
					size: item.size,
					mime_type: item.type,
					original_url: item.preview,
					preview_url: item.preview,
					updated_at: new Date(item.lastModified).toISOString(),
				};

		if (
			item.type === "application/vnd.ms-excel" ||
			item.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
			item.type?.startsWith("audio/")
		) {
			return false;
		}
		modal.openModal({
			title: "File Preview",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <FileViewer attachment={attachment} />,
		});
	};
	const handleClickWithId = (item: AcceptedFile, index: number) => {
		const isValidFile = item.file?.original_url;
		const attachment = isValidFile
			? item.file
			: {
					name: item.name,
					size: item.size,
					mime_type: item.type,
					original_url: item.preview,
					preview_url: item.preview,
					updated_at: new Date(item.lastModified).toISOString(),
				};

		modal.openModal({
			title: "File Preview",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <EvidenceFileViewer attachment={attachment} evidenceData={data?.[index]} />,
		});
	};
	const handleClickAffidavit = (item: AcceptedFile, index: number) => {
		const isValidFile = item.original_url;
		const attachment = isValidFile
			? item
			: {
					name: item.name,
					size: item.size,
					mime_type: item.type,
					original_url: item.preview,
					preview_url: item.preview,
					updated_at: new Date(item.lastModified).toISOString(),
				};

		modal.openModal({
			title: "File Preview",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <FileViewer attachment={attachment} />,
		});
	};
	const handleUpdateMarking = (index: number) => {
		modal.openModal({
			title: "Evidence Marking Status",
			description: "Update evidence marking status",
			component: <CaseEvidenceUpdateMarkingStatusModal data={data?.[index]} />,
		});
	};
	const handleUpdateAdmission = (index: number) => {
		modal.openModal({
			title: "Evidence Admission Status",
			description: "Update evidence admission status",
			component: <CaseEvidenceUpdateAdmissionStatusModal data={data?.[index]} />,
		});
	};
	const handleEdit = (index: number) => {
		modal.openModal({
			title: "Edit Evidence",
			description: `Update Evidence Exhibit ${form.getValues(`files.${index}.exhibit`)}`,
			component: <CaseEvidenceEditModal data={data?.[index]} />,
		});
	};
	const handleEditTestimonial = (index: number) => {
		modal.openModal({
			title: "Edit Details",
			description: `Edit Witness Details ${form.getValues(`files.${index}.witness_name`)}`,
			component: <CaseEvidenceEditModal data={data?.[index]} />,
		});
	};
	const handleEditSubmarking = (index: number) => {
		modal.openModal({
			title: "Edit Evidence",
			description: `Update Evidence Exhibit ${form.getValues(`files.${index}.exhibit`)}`,
			component: <CaseEvidenceEditModal data={undefined} submarking={data?.[index]} />,
		});
	};
	const handleAddTsn = (index: number) => {
		modal.openModal({
			title: "Upload Transcript of Stenographic Notes (TSN)",
			description: `Update TSN of ${form.getValues(`files.${index}.witness_name`)}`,
			component: <CaseTestimonialEvidenceFileUploadModal data={data?.[index]} />,
		});
	};
	const handleReplaceAttachment = (index: number) => {
		modal.openModal({
			title: "Replace Evidence ",
			description: `Replace Attachment of ${form.getValues(`files.${index}.exhibit`) || form.getValues(`files.${index}.witness_name`)}`,
			component: <CaseTestimonialEvidenceFileUploadModal data={data?.[index]} evidenceAttachment={true} />,
		});
	};
	const handleAddCategory = (index: number) => {
		modal.openModal({
			title: "Tag Evidence Category ",
			description: `Tag Category for ${form.getValues(`files.${index}.exhibit`) || form.getValues(`files.${index}.witness_name`)}`,
			component: <CaseEvidenceAddCategoryModal data={data?.[index]} />,
		});
	};
	const handleAddJudicialAffidavit = (index: number) => {
		modal.openModal({
			title: "Judicial Affidavit",
			description: `Add Judicial Affidavit of ${form.getValues(`files.${index}.exhibit`) || form.getValues(`files.${index}.witness_name`)}`,
			component: <CaseTestimonialEvidenceFileUploadModal data={data?.[index]} evidenceAttachment={true} judicialAffidavit={true} />,
		});
	};
	const handleAddSupplementaryJA = (index: number) => {
		modal.openModal({
			title: "Upload Supplementary Judicial Affidavit",
			description: `Add Supplementary Judicial Affidavit of ${form.getValues(`files.${index}.witness_name`)}`,
			component: <CaseTestimonialEvidenceFileUploadModal data={data?.[index]} affidavit={true} />,
		});
	};
	const handleRemoveRow = (row: EvidenceListAndIndex) => {
		if (!row.id) {
			const idToRemove = row?.file?._id ?? row?._id;

			const currentFiles = form.getValues("files");
			const updatedFiles = currentFiles.filter((f) => f._id !== idToRemove && f.parent_id !== idToRemove);
			form.setValue("files", updatedFiles, { shouldValidate: true });
		}
	};
	const medias = form.getValues("files") as AcceptedFile[];
	const parentId = form.getValues(`files.${index}.parent_id`);
	const parent = parentId ? medias.find((f) => f._id === parentId || f.id === parentId) : null;
	const parentExhibit = parent?.exhibit || form.getValues(`files.${index}.exhibit`);
	const parentWitnessName = parent?.witness_name || form.getValues(`files.${index}.witness_name`);

	const markingStatus = form.getValues(`files.${index}.marking_status`);
	const isValidMarkingStatus = markingStatus && markingStatus !== "-" && markingStatus !== "";
	const admissionStatus = form.getValues(`files.${index}.admission_status`);
	const isValidAdmissionStatus = admissionStatus && admissionStatus !== "-" && admissionStatus !== "";

	const affidavits = Array.isArray(data?.[index]?.affidavit) ? data?.[index]?.affidavit : [data?.[index]?.affidavit];

	const expungeCaseMutation = useEvidenceMutation();

	const storedTime = sessionStorage.getItem("password_entered_at");
	const handleExpunge = async (row: AcceptedFile) => {
		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			modal.openModal({
				type: "confirm",
				title: "Confirm Action",
				variant: "red",
				description: "Are you sure you want to expunge this item?",
				onConfirm: async () => {
					await expungeCaseMutation.mutateAsync({
						...row,
						status: "Expunged",
						case_id: data?.[0].case_id,
						evidence_type: metadata.evidence_type,
						parties: metadata.parties,
						case_record_user_id: metadata.case_record_user_id,
					});
				},
			});
		} else {
			try {
				modal.openModal({
					allowClose: true,
					title: "Confirm Submit",
					description: "Are you sure? This action cannot be undone.",
					size: "md",
					showHeader: true,
					showFooter: false,
					component: (
						<ConfirmWithPasswordModal
							onSubmit={async () => {
								await expungeCaseMutation.mutateAsync({
									...row,
									status: "Expunged",
									case_id: data?.[0].case_id,
									evidence_type: metadata.evidence_type,
									parties: metadata.parties,
									case_record_user_id: metadata.case_record_user_id,
								});
							}}
						/>
					),
				});
			} catch (error) {
				console.log(error);
			}
		}
	};

	const handleBulkEdit = () => {
		setIsEdit(true);
		setIsEditing(true);
	};

	const notesQuery = useQuery(
		caseEvidenceNoteQueryOptions(
			{
				case_evidence_id: data?.[index]?.id,
			},
			{
				enabled: !!data?.[index]?.id,
			},
		),
	);

	const notes = notesQuery?.data?.data;
	const { user } = useAuth();
	const isUserBcc = user?.is_bcc;
	const isUserOcc = user?.is_occ;
	const isUserCounsel = user?.is_counsel;
	const isVisibile = isUserBcc || isUserOcc;

	const isNoteSharedWithUser = notes?.some((note) => note.share_with?.includes(user?.id)) ?? null;

	const handleAddEvidenceNote = (index: number) => {
		modal.openModal({
			size: "lg",
			showHeader: false,
			showFooter: false,
			component: <CaseEvidenceNotesModal evidence={data?.[index]} caseRecord={caseData} branchId={caseData.branch_id} />,
		});
	};

	const stenographyQuery = useQuery(
		caseEvidenceListTsnQueryOptions(
			{
				case_id: data?.[index]?.case_id,
				id: data?.[index]?.id,
			},
			{
				enabled: metadata.evidence_type === "Testimonial",
			},
		),
	);
	const stenography = stenographyQuery.data;

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={clsx("flex flex-col w-full gap-1 py-2", {
				hidden: deletedData?.some((d) => d.id === form.getValues(`files.${index}.id`)),
			})}
		>
			<button
				type="button"
				onClick={toggleShowChild}
				className="absolute right-full top-2.5 mr-6 inline-flex justify-center items-center w-[18px] h-[18px] rounded-full shrink-0 text-foreground/50 hover:text-foreground bg-background border border-border/50"
			>
				{showChild ? <IconMinus size={14} stroke={2} /> : <IconPlus size={14} stroke={2} />}
			</button>
			<div className="z-[1]">
				<div className="flex flex-row items-center gap-2 w-full justify-between">
					<div className="flex flex-row items-center gap-2">
						<button
							type="button"
							{...attributes}
							{...listeners}
							className="inline-flex shrink-0 text-foreground/50 hover:text-foreground bg-background border-2 border-background"
						>
							<IconGripVertical size={16} stroke={2} />
						</button>
						<div className="inline-flex items-center gap-2">
							<div className="text-sm font-semibold text-foreground-dark">
								{form.getValues(`files.${index}.parent_id`) ? (
									form.getValues(`files.${index}.parent_id`) && form.getValues(`files.${index}.file`) ? (
										<Tooltip
											className="z-[90]"
											content={
												<div className="flex flex-col w-full gap-1">
													<div className="text-foreground-dark/90">Date Uploaded</div>
													<div className="text-foreground-dark/70 font-medium">{form.getValues(`files.${index}.created_at`)}</div>
												</div>
											}
											side="top"
										>
											<button
												className="font-semibold text-blue-600 hover:text-blue-500"
												type="button"
												onClick={() => {
													!form.getValues(`files.${index}.id`) ? handleClick(file) : handleClick(file);
												}}
											>
												{`Submarking of Exhibit ${parentExhibit || parentWitnessName} (${form.getValues(`files.${index}.exhibit`)})`}
											</button>
										</Tooltip>
									) : (
										<span className="text-sm font-semibold text-foreground-dark">{`Submarking of Exhibit ${parentExhibit || parentWitnessName} (${form.getValues(`files.${index}.exhibit`)})`}</span>
									)
								) : (
									<>
										{!form.getValues(`files.${index}.id`) && !form.getValues(`files.${index}.parent_id`) && form.getValues(`files.${index}.file`) ? (
											<Tooltip
												className="z-[90]"
												content={
													<div className="flex flex-col w-full gap-1">
														<div className="text-foreground-dark/90">Date Uploaded</div>
														<div className="text-foreground-dark/70 font-medium">{form.getValues(`files.${index}.created_at`)}</div>
													</div>
												}
												side="top"
											>
												<button
													className="cursor-pointer inline-flex flex-row items-center gap-1 p-1 pr-2 w-auto rounded-md whitespace-nowrap bg-blue-50 hover:bg-blue-100"
													type="button"
													onClick={() => {
														!form.getValues(`files.${index}.id`) ? handleClick(file) : handleClickWithId(file, index);
													}}
												>
													<IconFile size={18} stroke={2} className="inline-flex text-blue-600" />
													<span className="text-sm font-semibold text-blue-600">{file.name}</span>
												</button>
											</Tooltip>
										) : metadata.evidence_type !== "Testimonial" ? (
											// exhibit name clickable documnetary/object tab
											!onEdit && form.getValues(`files.${index}.id`) ? (
												<Tooltip
													className="z-[90]"
													content={
														<div className="flex flex-col w-full gap-1">
															<div className="text-foreground-dark/90">Date Uploaded</div>
															<div className="text-foreground-dark/70 font-medium">{form.getValues(`files.${index}.created_at`)}</div>
														</div>
													}
													side="top"
												>
													<button
														className="font-semibold text-blue-600 hover:text-blue-500"
														type="button"
														onClick={() => {
															!form.getValues(`files.${index}.id`) ? handleClick(file) : handleClickWithId(file, index);
														}}
													>
														Exhibit {form.getValues(`files.${index}.exhibit`) || form.getValues(`files.${index}.witness_name`)}
													</button>
												</Tooltip>
											) : (
												<FormField
													control={form.control}
													name={`files.${index}.exhibit`}
													render={({ field }) => (
														<FormItem>
															<Input
																className={clsx("px-2 py-1 min-h-[18px] h-full text-sm text-foreground-dark/75 outline-none ring-0 focus:ring-0", {
																	"cursor-not-allowed bg-muted/40 text-muted-foreground":
																		!onEdit || !isEditing || removeFromList || form.getValues(`files.${index}.status`) === "Expunged",
																})}
																defaultValue={field.value}
																maxLength={1000}
																disabled={!onEdit || !isEditing || removeFromList || form.getValues(`files.${index}.status`) === "Expunged"}
																{...field}
															/>
														</FormItem>
													)}
												/>
											)
										) : (
											<div className="inline-block flex-row">
												{form.getValues(`files.${index}.file`) !== null ? (
													<span className="pr-1">
														{/* testimonial */}
														<Tooltip
															className="z-[90]"
															content={
																<div className="flex flex-col w-full gap-1">
																	<div className="text-foreground-dark/90">Date Uploaded</div>
																	<div className="text-foreground-dark/70 font-medium">{form.getValues(`files.${index}.created_at`)}</div>
																</div>
															}
															side="top"
														>
															<button
																className="cursor-pointer inline-flex flex-row items-center gap-1 p-1 pr-2 mb-1 w-auto rounded-md whitespace-nowrap bg-blue-50 hover:bg-blue-100"
																type="button"
																onClick={() => {
																	!form.getValues(`files.${index}.id`) ? handleClick(file) : handleClickWithId(file, index);
																}}
															>
																<IconFile size={18} stroke={2} className="inline-flex text-blue-600" />
																<span className="text-sm font-semibold text-blue-600">{file.name || "witnes"}</span>
															</button>
														</Tooltip>
													</span>
												) : (
													<span className="pr-1">
														{/* testimonial */}
														<span className="text-sm font-semibold text-foreground-dark">{form.getValues(`files.${index}.witness_name`)}</span>
													</span>
												)}
												{/* supplementary affidavit */}
												{form.getValues(`files.${index}.id`) &&
													form.getValues(`files.${index}.affidavit`) !== null &&
													metadata.evidence_type === "Testimonial" &&
													form.getValues(`files.${index}.affidavit`)?.map((affidavit, index) => (
														<span key={index} className="pr-1">
															<Tooltip
																className="z-[90]"
																content={
																	<div className="flex flex-col w-full gap-1">
																		<div className="text-foreground-dark/90">Date Uploaded</div>
																		<div className="text-foreground-dark/70 font-medium">{formatDate(affidavit?.created_at)}</div>
																	</div>
																}
																side="top"
															>
																<button
																	className="cursor-pointer inline-flex flex-row items-center gap-1 p-1 pr-2 mb-1 w-auto rounded-md whitespace-nowrap bg-blue-50 hover:bg-blue-100"
																	type="button"
																	onClick={() => {
																		!form.getValues(`files.${index}.id`) ? handleClickAffidavit(affidavit, index) : handleClickAffidavit(affidavit, index);
																	}}
																>
																	<IconFile size={18} stroke={2} className="inline-flex text-blue-600" />
																	<span className="text-sm font-semibold text-blue-600">{affidavit?.name}</span>
																</button>
															</Tooltip>
														</span>
													))}
											</div>
										)}
									</>
								)}
							</div>
							<div className="text-sm font-semibold text-foreground-dark">
								<div className="inline-flex flex-row items-center w-auto gap-1">
									{form.getValues(`files.${index}.status`) === "Expunged" && (
										<Tooltip
											className="z-[90]"
											content={
												<div className="flex flex-col w-full gap-1">
													<div className="text-foreground-dark/90">Expunged Date</div>
													<div className="text-foreground-dark/70 font-medium">{form.getValues(`files.${index}.expunged_at`)}</div>
												</div>
											}
											side="top"
										>
											<div className="inline-flex items-center h-[18px] px-2 rounded-full bg-red-600/10 text-red-600">
												<span className="text-xs font-semibold">Expunged</span>
											</div>
										</Tooltip>
									)}
								</div>
							</div>
						</div>
					</div>

					{!displayOnly && (
						<div>
							{/* comment button */}
							{(isVisibile || !isUserCounsel || isNoteSharedWithUser) && !removeFromList && form.getValues(`files.${index}.id`) && (
								<Button
									className="!rounded-full flex-row mr-2"
									type="button"
									size="icon"
									variant="ghost"
									onClick={() => {
										handleAddEvidenceNote(index);
									}}
								>
									<IconMessageDots size={14} stroke={2} />
								</Button>
							)}
							{/* dropdown */}
							{/* parent */}
							{form.getValues(`files.${index}.status`) !== "Expunged" && !removeFromList && !form.getValues(`files.${index}.parent_id`) && !onEdit && (
								<DropdownMenu modal={false}>
									<DropdownMenuTrigger asChild>
										<Button className="shrink-0 !rounded-full !w-[24px] !h-[24px]" size="icon" variant="ghost">
											<IconDots size={16} stroke={1.5} />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										{/* with id */}
										{form.getValues(`files.${index}.id`) && (
											<>
												{metadata.evidence_type !== "Testimonial" ? (
													<DropdownMenuItem onClick={() => handleEdit(index)}>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span className="">Edit</span>
														</div>
													</DropdownMenuItem>
												) : (
													<DropdownMenuItem onClick={() => handleEditTestimonial(index)}>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span className="">Edit</span>
														</div>
													</DropdownMenuItem>
												)}
												<DropdownMenuItem onClick={handleBulkEdit}>
													<div className="flex flex-row items-center gap-2 px-2 py-1">
														<span className="">Edit All Evidence</span>
													</div>
												</DropdownMenuItem>
												<DropdownMenuItem
													onSelect={() => {
														handleReplaceAttachment(index);
													}}
												>
													<div className="flex flex-row items-center gap-2 px-2 py-1">
														<span>Replace Attachment</span>
													</div>
												</DropdownMenuItem>
												<DropdownMenuItem
													onSelect={() => {
														handleAddCategory(index);
													}}
												>
													<div className="flex flex-row items-center gap-2 px-2 py-1">
														<span>Tag Category</span>
													</div>
												</DropdownMenuItem>
											</>
										)}
										{metadata.evidence_type !== "Testimonial" && (
											<DropdownMenuItem
												onSelect={() => {
													setShowChild(true);
													const fileData: AcceptedFile = {
														file: null,
														_id: uuidv4(),
														parent_id: file._id,
														name: `Submarking of ${form.getValues(`files.${index}.exhibit`)}`,
														_depth: 0,
														_lft: 0,
														_rgt: 0,
														size: 0,
														type: null,
														preview: null,
														lastModified: Date.now(),
														...metadata,
													};
													handleAddSubmarking?.(true);
													append(fileData);
												}}
											>
												<div className="flex flex-row items-center gap-2 px-2 py-1">
													<span>Add Submarking</span>
												</div>
											</DropdownMenuItem>
										)}
										{/* with id, not testimonial */}
										{form.getValues(`files.${index}.id`) && metadata.evidence_type !== "Testimonial" && (
											<DropdownMenuGroup>
												<HasPermission permissions={[userPermissions.UPDATE_EVIDENCE]}>
													<DropdownMenuItem
														onSelect={() => {
															handleUpdateMarking(index);
														}}
													>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span className="translate-y-[-1px] leading-1">Update Marking Status</span>
														</div>
													</DropdownMenuItem>
												</HasPermission>
												<HasPermission permissions={[userPermissions.UPDATE_EVIDENCE]}>
													<DropdownMenuItem
														onSelect={() => {
															handleUpdateAdmission(index);
														}}
													>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span className="translate-y-[-1px] leading-1">Update Admission Status</span>
														</div>
													</DropdownMenuItem>
												</HasPermission>
											</DropdownMenuGroup>
										)}
										{metadata?.evidence_type === "Testimonial" && form.getValues(`files.${index}.id`) && (
											<DropdownMenuItem
												onSelect={() => {
													handleAddSupplementaryJA(index);
												}}
											>
												<div className="flex flex-row items-center gap-2 px-2 py-1">
													<span>Add Supplementary J.A.</span>
												</div>
											</DropdownMenuItem>
										)}
										{!file.name && form.getValues(`files.${index}.id`) && metadata?.evidence_type === "Testimonial" && (
											<DropdownMenuItem
												onSelect={() => {
													handleAddJudicialAffidavit(index);
												}}
											>
												<div className="flex flex-row items-center gap-2 px-2 py-1">
													<span>Add Judicial Affidavit</span>
												</div>
											</DropdownMenuItem>
										)}
										<DropdownMenuSeparator />
										{form.getValues(`files.${index}.id`) ? (
											<>
												<DropdownMenuItem onClick={() => handleExpunge(form.getValues(`files.${index}`))} dangerZone>
													<div className="flex flex-row items-center gap-2 px-2 py-1">
														<span>Expunge</span>
													</div>
												</DropdownMenuItem>
											</>
										) : (
											<DropdownMenuItem onClick={() => handleRemoveRow?.({ ...form.getValues(`files.${index}`), index })} dangerZone>
												<div className="flex flex-row items-center gap-2 px-2 py-1">
													<span>Remove from list</span>
												</div>
											</DropdownMenuItem>
										)}
									</DropdownMenuContent>
								</DropdownMenu>
							)}
							{/* child */}
							{form.getValues(`files.${index}.id`) &&
								form.getValues(`files.${index}.status`) !== "Expunged" &&
								!removeFromList &&
								!onEdit &&
								form.getValues(`files.${index}.parent_id`) && (
									<DropdownMenu modal={false}>
										<DropdownMenuTrigger asChild>
											<Button className="shrink-0 !rounded-full !w-[24px] !h-[24px]" size="icon" variant="ghost">
												<IconDots size={16} stroke={1.5} />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent>
											{/* with id && with parent id */}
											{form.getValues(`files.${index}.id`) && form.getValues(`files.${index}.parent_id`) && (
												<>
													<DropdownMenuItem onClick={() => handleEditSubmarking(index)}>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span className="">Edit</span>
														</div>
													</DropdownMenuItem>
													<DropdownMenuItem
														onSelect={() => {
															handleAddCategory(index);
														}}
													>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span>Tag Category</span>
														</div>
													</DropdownMenuItem>
												</>
											)}
											{/* with id, not testimonial */}
											{form.getValues(`files.${index}.id`) && metadata.evidence_type !== "Testimonial" && (
												<DropdownMenuGroup>
													<HasPermission permissions={[userPermissions.UPDATE_EVIDENCE]}>
														<DropdownMenuItem
															onSelect={() => {
																handleUpdateMarking(index);
															}}
														>
															<div className="flex flex-row items-center gap-2 px-2 py-1">
																<span className="translate-y-[-1px] leading-1">Update Marking Status</span>
															</div>
														</DropdownMenuItem>
													</HasPermission>
													<HasPermission permissions={[userPermissions.UPDATE_EVIDENCE]}>
														<DropdownMenuItem
															onSelect={() => {
																handleUpdateAdmission(index);
															}}
														>
															<div className="flex flex-row items-center gap-2 px-2 py-1">
																<span className="translate-y-[-1px] leading-1">Update Admission Status</span>
															</div>
														</DropdownMenuItem>
													</HasPermission>
												</DropdownMenuGroup>
											)}
											<DropdownMenuSeparator />
											{form.getValues(`files.${index}.id`) && !removeFromList && (
												<DropdownMenuItem onClick={() => handleExpunge(form.getValues(`files.${index}`))} dangerZone>
													<div className="flex flex-row items-center gap-2 px-2 py-1">
														<span>Expunge</span>
													</div>
												</DropdownMenuItem>
											)}
											{!form.getValues(`files.${index}.id`) && !removeFromList && (
												<DropdownMenuItem onClick={() => handleExpunge(form.getValues(`files.${index}`))} dangerZone>
													<div className="flex flex-row items-center gap-2 px-2 py-1">
														<span>Delete</span>
													</div>
												</DropdownMenuItem>
											)}
										</DropdownMenuContent>
									</DropdownMenu>
								)}
							{/* onEdit */}
							{!removeFromList && form.getValues(`files.${index}.status`) !== "Expunged" && onEdit && (
								<DropdownMenu modal={false}>
									<DropdownMenuTrigger asChild>
										<Button className="shrink-0 !rounded-full !w-[24px] !h-[24px]" size="icon" variant="ghost">
											<IconDots size={16} stroke={1.5} />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										{!form.getValues(`files.${index}.parent_id`) && metadata.evidence_type !== "Testimonial" && (
											<>
												<DropdownMenuItem
													onSelect={() => {
														setShowChild(true);
														const fileData: AcceptedFile = {
															file: null,
															_id: uuidv4(),
															parent_id: file._id,
															name: `Submarking of ${form.getValues(`files.${index}.exhibit`)}`,
															_depth: 0,
															_lft: 0,
															_rgt: 0,
															size: 0,
															type: null,
															preview: null,
															lastModified: Date.now(),
															...metadata,
														};
														append(fileData);
													}}
												>
													<div className="flex flex-row items-center gap-2 px-2 py-1">
														<span>Add Submarking</span>
													</div>
												</DropdownMenuItem>
												<DropdownMenuSeparator />
											</>
										)}
										<DropdownMenuItem
											onClick={() => {
												handleRemoveRow?.({ ...form.getValues(`files.${index}`), index });
											}}
											dangerZone
										>
											<div className="flex flex-row items-center gap-2 px-2 py-1">
												<span>Remove from list</span>
											</div>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
							{/* child no id */}
							{!removeFromList &&
								!form.getValues(`files.${index}.id`) &&
								form.getValues(`files.${index}.status`) !== "Expunged" &&
								!onEdit &&
								form.getValues(`files.${index}.parent_id`) && (
									<DropdownMenu modal={false}>
										<DropdownMenuTrigger asChild>
											<Button className="shrink-0 !rounded-full !w-[24px] !h-[24px]" size="icon" variant="ghost">
												<IconDots size={16} stroke={1.5} />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent>
											<DropdownMenuItem
												onClick={() => {
													handleRemoveRow?.({ ...form.getValues(`files.${index}`), index });
												}}
												dangerZone
											>
												<div className="flex flex-row items-center gap-2 px-2 py-1">
													<span>Remove from list</span>
												</div>
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								)}
							{/* parent and amend */}
							{removeFromList === true && form.getValues(`files.${index}.status`) !== "Expunged" && (
								<DropdownMenu modal={false}>
									<DropdownMenuTrigger asChild>
										<Button className="shrink-0 !rounded-full !w-[24px] !h-[24px]" size="icon" variant="ghost">
											<IconDots size={16} stroke={1.5} />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										{!form.getValues(`files.${index}.parent_id`) &&
											form.getValues(`files.${index}.exhibit`) !== "" &&
											metadata.evidence_type !== "Testimonial" && (
												// for parent and non testimonial
												<DropdownMenuItem
													onSelect={() => {
														setShowChild(true);
														const fileData: AcceptedFile = {
															file: null,
															_id: uuidv4(),
															parent_id: file._id,
															name: `Submarking of ${form.getValues(`files.${index}.exhibit`)}`,
															_depth: 0,
															_lft: 0,
															_rgt: 0,
															size: 0,
															type: null,
															preview: null,
															lastModified: Date.now(),
															...metadata,
														};
														handleAddSubmarking?.(true);
														append(fileData);
													}}
												>
													<div className="flex flex-row items-center gap-2 px-2 py-1">
														<span>Add Submarking</span>
													</div>
												</DropdownMenuItem>
											)}
										{removeFromList && form.getValues(`files.${index}.id`) && (
											<DropdownMenuItem
												disabled={metadata.parties === "Defendant"}
												onClick={() => {
													handleAmendDelete?.({ ...form.getValues(`files.${index}`) }, metadata?.evidence_type);
												}}
												dangerZone
											>
												<div className="flex flex-row items-center gap-2 px-2 py-1">
													<span>Remove from list</span>
												</div>
											</DropdownMenuItem>
										)}
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						</div>
					)}
				</div>
				<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden mt-2 z-[1]">
					<table className="w-full text-sm z-[1]">
						<tbody className="z-[1]">
							{schema
								.filter((field) => {
									const fileId = form.getValues(`files.${index}.id`);
									if (!fileId && (field.name === "marking_status" || field.name === "admission_status" || field.name === "category")) {
										return false;
									}
									return true;
								})
								.map((field) => (
									<Fragment key={field.name}>
										{field.name !== "exhibit" && form.getValues(`files.${index}.id`) && (
											<tr className="border-b border-border/50 last:border-b-0 bg-muted/40 z-[1]">
												<td className="w-full flex flex-row gap-1 items-center py-1 px-2 whitespace-nowrap z-[1]">{field.label}</td>
												<td className="w-full h-full p-0 z-[99]">
													{/* text input */}
													{field.type !== "dragged_file" &&
														field.type !== "file" &&
														field.type !== "marking_status" &&
														field.type !== "admission_status" &&
														field.name !== "category" &&
														(field.name !== "exhibit" ? (
															<TextareaAutosize
																maxRows={5}
																className={clsx(
																	"resize-none pt-1 text-sm text-gray-400 group-hover:text-gray-600 leading-tight !border-none !outline-none flex w-full text-gray-600 px-2 py-1 min-h-[18px] h-full text-sm text-foreground-dark/75 outline-none ring-0 focus:ring-0",
																	{
																		"cursor-not-allowed bg-muted/40 text-muted-foreground":
																			!onEdit || !isEditing || removeFromList || form.getValues(`files.${index}.status`) === "Expunged",
																	},
																)}
																maxLength={1000}
																// placeholder={`Add ${field.label}`}
																required={field.name === "description"}
																defaultValue={file[field.name] as string | undefined}
																disabled={!onEdit || !isEditing || removeFromList || form.getValues(`files.${index}.status`) === "Expunged"}
																{...form.register(`files.${index}.${field.name}`)}
															/>
														) : (
															<Input
																className={clsx("px-2 py-1 min-h-[18px] h-full text-sm text-foreground-dark/75 outline-none ring-0 focus:ring-0", {
																	"cursor-not-allowed bg-muted/40 text-muted-foreground":
																		!onEdit || !isEditing || removeFromList || form.getValues(`files.${index}.status`) === "Expunged",
																})}
																maxLength={1000}
																// placeholder={`Add ${field.label}`}
																defaultValue={file[field.name] as string | undefined}
																disabled={!onEdit || !isEditing || removeFromList || form.getValues(`files.${index}.status`) === "Expunged"}
																{...form.register(`files.${index}.${field.name}`, {
																	required: field.name === "exhibit",
																	validate: (value) => field.name !== "exhibit" || value.trim() !== "" || "This field is required",
																})}
															/>
														))}

													{/* marking status */}
													{field.type === "marking_status" && isValidMarkingStatus && !isEditing && !removeFromList ? (
														<Tooltip
															className="z-[90]"
															content={
																<div className="flex flex-col w-full gap-1">
																	<div className="text-foreground-dark/90">Marking Status Update Date</div>
																	<div className="text-foreground-dark/70 font-medium">{form.getValues(`files.${index}.updated_marking_status_at`)}</div>
																</div>
															}
															side="top"
														>
															<div className="inline-flex items-center h-[18px] px-2 rounded-full bg-green-600/10 text-green-600">
																<span className="text-xs font-semibold">{markingStatus}</span>
															</div>
														</Tooltip>
													) : (
														field.type === "marking_status" &&
														onEdit && (
															<div className="grid grid-cols-12 w-full py-1 gap-y-1">
																<div className="col-span-12 w-full">
																	<FormField
																		control={form.control}
																		name={`files.${index}.updated_marking_status_at`}
																		render={({ field }) => (
																			<FormItem>
																				<FormGroup inputSize="sm">
																					<Input className="!outline-none !rounded-none !border-none text-sm" autoComplete="off" type="date" {...field} />
																				</FormGroup>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>
																</div>
																<div className="col-span-12 w-full">
																	<FormField
																		control={form.control}
																		name={`files.${index}.marking_status`}
																		render={({ field }) => (
																			<FormItem>
																				<FormGroup>
																					<Select
																						size="sm"
																						className=""
																						isUnstyled={true}
																						onValueChange={async (value) => {
																							field.onChange(value);
																						}}
																						defaultValue={field.value}
																						options={[
																							{
																								value: "Provisional",
																								label: "Provisional",
																							},
																							{
																								value: "Permanent",
																								label: "Permanent",
																							},
																							{
																								value: "-",
																								label: "-",
																							},
																						]}
																					/>
																				</FormGroup>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>
																</div>
															</div>
														)
													)}
													{/* admission status */}
													{field.type === "admission_status" && isValidAdmissionStatus && !isEditing ? (
														<Tooltip
															className="z-[90]"
															content={
																<div className="flex flex-col w-full gap-1">
																	<div className="text-foreground-dark/90">Admission Status Update Date</div>
																	<div className="text-foreground-dark/70 font-medium">{form.getValues(`files.${index}.updated_admission_status_at`)}</div>
																</div>
															}
															side="top"
														>
															<div
																className={clsx("inline-flex items-center h-[18px] px-2 rounded-full", {
																	"bg-green-600/10 text-green-600": admissionStatus === "Admitted",
																	"bg-red-600/10 text-red-600": admissionStatus === "Denied",
																	"bg-orange-600/10 text-orange-600": admissionStatus === "Denied but Tendered",
																})}
															>
																<span className="text-xs font-semibold">{admissionStatus}</span>
															</div>
														</Tooltip>
													) : (
														field.type === "admission_status" &&
														onEdit && (
															<div className="grid grid-cols-12 w-full py-1 gap-y-2">
																<div className="col-span-12 w-full">
																	<FormField
																		control={form.control}
																		name={`files.${index}.updated_admission_status_at`}
																		render={({ field }) => (
																			<FormItem>
																				<FormGroup inputSize="sm">
																					<Input className="!outline-none !rounded-none !border-none text-sm" autoComplete="off" type="date" {...field} />
																				</FormGroup>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>
																</div>
																<div className="col-span-12 w-full">
																	<FormField
																		control={form.control}
																		name={`files.${index}.admission_status`}
																		render={({ field }) => (
																			<FormItem>
																				<FormGroup>
																					<Select
																						size="sm"
																						isUnstyled={true}
																						menuPlacement="top"
																						onValueChange={async (value) => {
																							field.onChange(value);
																						}}
																						defaultValue={field.value}
																						options={[
																							{
																								value: "Admitted",
																								label: "Admitted",
																							},
																							{
																								value: "Denied",
																								label: "Denied",
																							},
																							{
																								value: "Denied but Tendered",
																								label: "Denied but Tendered...",
																							},
																							{
																								value: "-",
																								label: "-",
																							},
																						]}
																					/>
																				</FormGroup>
																				<FormMessage />
																			</FormItem>
																		)}
																	/>
																</div>
															</div>
														)
													)}
													{field.type === "dragged_file" && form.getValues(`files.${index}.id`) && (
														<Tooltip
															className="z-[90]"
															content={
																<div className="flex flex-col w-full gap-1">
																	<div className="text-foreground-dark/90">Date Uploaded</div>
																	<div className="text-foreground-dark/70 font-medium">{form.getValues(`files.${index}.created_at`)}</div>
																</div>
															}
															side="top"
														>
															<span className="text-sm text-foreground-dark">
																{/* {truncateName(file.name)} */}
																{file.name}
															</span>
														</Tooltip>
													)}
													{form.getValues(`files.${index}.id`) &&
														metadata.evidence_type === "Testimonial" &&
														field.type === "dragged_file" &&
														affidavits.map((affidavit, index) => (
															<Tooltip
																key={index}
																className="z-[90]"
																content={
																	<div className="flex flex-col w-full gap-1">
																		<div className="text-foreground-dark/90">Date Uploaded</div>
																		<div className="text-foreground-dark/70 font-medium">{formatDate(affidavit?.created_at)}</div>
																	</div>
																}
																side="top"
															>
																<button
																	className="font-semibold text-blue-600 hover:text-blue-500"
																	type="button"
																	onClick={() => {
																		!form.getValues(`files.${index}.id`) ? handleClickAffidavit(affidavit, index) : handleClickAffidavit(affidavit, index);
																	}}
																>
																	{affidavit.name}
																</button>
															</Tooltip>
														))}
													<div className="col-span-12 w-full">
														{field.name === "stenography" &&
															stenography?.length !== 0 &&
															stenography?.map(
																(v: CaseActivity) =>
																	v?.media?.length !== 0 && (
																		<div className="flex flex-row py-1 gap-3 flex-wrap">
																			<button
																				key={v?.media?.[0]?.id}
																				className="min-h-[18px] text-sm text-left outline-none ring-0 focus:ring-0 text-blue-600 font-semibold hover:text-blue-500 p-0 px-2 py-1"
																				type="button"
																				onClick={() => handleClickStenography(v?.media?.[0])}
																			>
																				{v.media?.[0]?.name}
																			</button>
																		</div>
																	),
															)}
													</div>
													<div className="flex flex-row gap-2">
														{form.getValues(`files.${index}.id`) &&
															field.name === "category" &&
															data?.[index]?.category !== null &&
															data?.[index].category.map((value: { label: string; value: string }) => (
																<div
																	key={value.value}
																	className="shrink-0 justify-center items-center min-w-[18px] h-[18px] px-2 rounded-full bg-slate-200 text-xs font-bold text-foreground"
																>
																	{value.value}
																</div>
															))}
													</div>
												</td>
											</tr>
										)}
										{!form.getValues(`files.${index}.id`) && (
											<tr className="border-b border-border/50 last:border-b-0 bg-muted/40">
												<td className="w-full flex flex-row gap-1 items-center py-1 px-2 whitespace-nowrap">
													{field.label}
													{!form.getValues(`files.${index}.id`) && field.name === "exhibit" && (
														<span className="text-danger-600">
															<IconAsteriskSimple size={8} stroke={3} />
														</span>
													)}
													{!form.getValues(`files.${index}.id`) && field.name === "description" && (
														<span className="text-danger-600">
															<IconAsteriskSimple size={9} stroke={3} />
														</span>
													)}
													{!form.getValues(`files.${index}.id`) && field.name === "witness_name" && (
														<span className="text-danger-600">
															<IconAsteriskSimple size={9} stroke={3} />
														</span>
													)}
												</td>
												<td className="w-full h-full p-0">
													{/* text input */}
													{field.type !== "dragged_file" &&
														field.type !== "file" &&
														field.type !== "marking_status" &&
														field.type !== "admission_status" &&
														(field.name === "purpose" || field.name === "description" ? (
															<TextareaAutosize
																maxRows={5}
																className={clsx(
																	"resize-none pt-1 text-sm text-gray-400 group-hover:text-gray-600 leading-tight !border-none !outline-none flex w-full text-gray-600 px-2 py-1 min-h-[18px] h-full text-sm text-foreground-dark/75 outline-none ring-0 focus:ring-0",
																	{
																		"cursor-not-allowed bg-muted/40 text-muted-foreground":
																			form.getValues(`files.${index}.id`) || form.getValues(`files.${index}.status`) === "Expunged",
																	},
																)}
																maxLength={1000}
																placeholder={`Add ${field.label}`}
																required={field.name === "description"}
																defaultValue={file[field.name] as string | undefined}
																disabled={form.getValues(`files.${index}.id`) || form.getValues(`files.${index}.status`) === "Expunged"}
																{...form.register(`files.${index}.${field.name}`)}
															/>
														) : (
															<Input
																className={clsx("px-2 py-1 min-h-[18px] h-full text-sm text-foreground-dark/75 outline-none ring-0 focus:ring-0", {
																	"cursor-not-allowed bg-muted/40 text-muted-foreground":
																		form.getValues(`files.${index}.id`) || form.getValues(`files.${index}.status`) === "Expunged",
																})}
																maxLength={1000}
																placeholder={`Add ${field.label}`}
																defaultValue={file[field.name] as string | undefined}
																disabled={form.getValues(`files.${index}.id`) || form.getValues(`files.${index}.status`) === "Expunged"}
																{...form.register(`files.${index}.${field.name}`, {
																	required: field.name === "exhibit",
																	validate: (value) => field.name !== "exhibit" || value.trim() !== "" || "This field is required",
																})}
															/>
														))}
													{field.type === "dragged_file" && (
														<Tooltip
															className="z-[90]"
															content={
																<div className="flex flex-col w-full gap-1">
																	<div className="text-foreground-dark/90">Date Uploaded</div>
																	<div className="text-foreground-dark/70 font-medium">{form.getValues(`files.${index}.created_at`)}</div>
																</div>
															}
															side="top"
														>
															<button
																className="font-semibold text-blue-600 hover:text-blue-500"
																type="button"
																onClick={() => {
																	!form.getValues(`files.${index}.id`) ? handleClick(file) : handleClickWithId(file, index);
																}}
															>
																{file.name}
															</button>
														</Tooltip>
													)}
												</td>
											</tr>
										)}
									</Fragment>
								))}
						</tbody>
					</table>
				</div>
			</div>
			{showChild && allFiles && allFiles.length > 0 && (
				<div className="flex flex-col w-full pl-8 gap-2 border-l border-dashed border-border mt-2">
					{/* create child */}
					{allFiles
						.filter((a) => a.parent_id === file.id && a.parent_id !== null)
						.map((childFile) => {
							const childFlatIndex = allFiles.findIndex((f) => f._id === childFile._id);
							return (
								<EvidenceItem
									handleAmendDelete={handleAmendDelete}
									deletedData={deletedData}
									data={data}
									caseData={caseData}
									key={childFile._id}
									allFiles={allFiles}
									index={childFlatIndex}
									file={childFile}
									form={form}
									schema={schema}
									isDragging={isDragging}
									metadata={metadata}
									onEdit={onEdit}
									setIsEdit={setIsEditing}
									// If needed, implement deletion for children here
									onDelete={() => {
										!form.getValues(`files.${index}.id`)
											? modal.openModal({
													type: "confirm",
													title: "Confirm Action",
													variant: "red",
													description: "Are you sure you want to remove this item?",
													onConfirm: async () => {
														remove(childFlatIndex);
													},
												})
											: handleExpunge(form.getValues(`files.${index}`));
									}}
									removeFromList={removeFromList}
									handleAddSubmarking={handleAddSubmarking}
								/>
							);
						})}
					{/* display child*/}
					{files?.map((childFile) => {
						const childFlatIndex = allFiles.findIndex((f) => f._id === childFile._id);

						return (
							<EvidenceItem
								handleAmendDelete={handleAmendDelete}
								deletedData={deletedData}
								data={data}
								caseData={caseData}
								key={childFile._id}
								allFiles={allFiles}
								index={childFlatIndex}
								file={childFile}
								form={form}
								schema={schema}
								isDragging={isDragging}
								metadata={metadata}
								onEdit={onEdit}
								setIsEdit={setIsEditing}
								// If needed, implement deletion for children here
								onDelete={() => {
									!form.getValues(`files.${index}.id`)
										? modal.openModal({
												type: "confirm",
												title: "Confirm Action",
												variant: "red",
												description: "Are you sure you want to remove this item?",
												onConfirm: async () => {
													remove(childFlatIndex);
												},
											})
										: handleExpunge(form.getValues(`files.${index}`));
								}}
								removeFromList={removeFromList}
								handleAddSubmarking={handleAddSubmarking}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

// -------------------------------------------------------
// The EvidenceList component passes the full flat array to each EvidenceItem.
// -------------------------------------------------------
export default function EvidenceList({
	data,
	caseData,
	metadata,
	schema,
	removeFromList,
	onEditMode,
	acceptedDataTypes = [
		"application/pdf",
		"image/*",
		"video/mp4",
		"audio/*",
		"application/vnd.ms-excel",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	],
	onSubmit,
	onAdded,
	onRowDelete,
	className,
	handleAmendDelete,
	handleUpdateAffidavit,
	handleUpdateExhibit,
	deletedData,
	displayOnly,
}: EvidenceListProps) {
	const modal = useModal();
	const form = useForm<{ files: AcceptedFile[] }>({
		defaultValues: { files: [] },
	});
	const { fields, append, move } = useFieldArray({
		control: form.control,
		name: "files",
	});

	const files = useWatch({
		control: form.control,
		name: "files",
	});

	const [isDragging, setIsDragging] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [isNewEvidence, setAddNewEvidence] = useState(false);
	const [fileLength, setFilesLength] = useState(0);
	const hasReset = useRef(false);
	const handleDragStart = () => setIsDragging(true);
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [isAddingSubmarking, setAddingSubmarking] = useState<boolean | undefined>();

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = fields.findIndex((f) => f._id === active.id);
		const newIndex = fields.findIndex((f) => f._id === over.id);
		if (oldIndex !== -1 && newIndex !== -1) {
			move(oldIndex, newIndex);
		}
	};
	const handleRemoveRow = (row: EvidenceListAndIndex) => {
		if (!row.id) {
			const idToRemove = row?.file?._id ?? row?._id;

			const currentFiles = form.getValues("files");
			const updatedFiles = currentFiles.filter((f) => f._id !== idToRemove && f.parent_id !== idToRemove);
			form.setValue("files", updatedFiles, { shouldValidate: true });
		}
		if (row.id) {
			modal.openModal({
				type: "confirm",
				title: "Confirm Action",
				variant: "red",
				description: "Are you sure you want to expunge this item?",
				onConfirm: async () => {
					onRowDelete?.({ ...row, status: "Expunged" });
				},
			});
		}
	};

	const onFormActions = (values: { files: AcceptedFile[] }) => {
		if (onSubmit) {
			modal.openModal({
				type: "confirm",
				variant: "green",
				title: "Confirm Evidence Details",
				description: "Are you sure you want to submit this case? Please review the details before proceeding.",
				onConfirm: async () => {
					try {
						await onSubmit(values.files);
						setSubmitSuccess(true);
						setIsEditing(false);
						setAddingSubmarking(false);
					} catch (error) {
						setSubmitSuccess(false);
						console.error("Submission failed", error);
					}
				},
			});
		}
	};

	useEffect(() => {
		if (onEditMode) {
			if (fileLength !== 0 && isEditing) {
				onEditMode(true);
			}
			if (submitSuccess) {
				onEditMode(null);
			}
		}
	}, [isEditing, onEditMode, fileLength, submitSuccess]);

	useEffect(() => {
		if (onAdded) {
			onAdded?.(files);
		}
	}, [files]);

	useEffect(() => {
		if (onAdded) {
			setIsEditing(true);
		}
	}, [onAdded]);

	useEffect(() => {
		if (data && !hasReset.current) {
			const mappedFiles = mapDataToAcceptedFiles(data, schema);
			form.reset({ files: mappedFiles });
			hasReset.current = true;
		}
	}, [data, form.reset]);

	const { getRootProps, getInputProps, open } = useDropzone({
		accept: acceptedDataTypes.reduce(
			(acc, mime) => {
				acc[mime] = [];
				return acc;
			},
			{} as Record<string, string[]>,
		),
		noClick: true,
		noKeyboard: true,
		onDrop: (droppedFiles) => {
			setFilesLength(droppedFiles.length);
			droppedFiles.map((file) => {
				const id = uuidv4();
				const fileData: AcceptedFile = {
					file,
					_id: id,
					parent_id: null,
					name: file.name,
					size: file.size,
					type: file.type,
					status: file.status,
					lastModified: file.lastModified,
					preview: URL.createObjectURL(file),
				};
				// setIsEditing(true);
				setAddNewEvidence(true);
				append(fileData);
				return fileData;
			});
		},
	});

	const handleAddSubmarking = (value?: boolean) => {
		setAddingSubmarking(value);
	};

	useEffect(() => {
		handleAddSubmarking?.(isAddingSubmarking);
	}, [isAddingSubmarking, handleAddSubmarking, setAddingSubmarking]);

	useEffect(() => {
		if (displayOnly !== true) {
			displayOnly = false;
		}
	}, [displayOnly]);
	return (
		<Form {...form}>
			<form className="flex flex-col w-full h-full" onSubmit={form.handleSubmit(onFormActions)}>
				<div className={className}>
					<ScrollArea className="flex flex-col w-full">
						<div className="flex flex-col w-full p-4">
							{data?.length === 0 && fileLength === 0 && !displayOnly ? (
								<div {...getRootProps()} className="dropzone inline-flex flex-col justify-center items-center w-full min-h-[240px]">
									<input {...getInputProps()} />
									<div className="inline-flex flex-col justify-center items-center gap-2">
										<div className="inline-flex justify-center items-center w-[100px] h-[100px] p-2 rounded-full border border-border/30">
											<div className="inline-flex justify-center items-center w-full h-full p-2 rounded-full border border-border/50">
												<div className="inline-flex justify-center items-center w-full h-full p-2 rounded-full border border-border/70">
													<div className="inline-flex justify-center items-center w-full h-full rounded-full bg-border/50 text-foreground">
														<IconDragDrop size={24} stroke={1.5} />
													</div>
												</div>
											</div>
										</div>
										<p className="max-w-[320px] text-center text-base text-foreground/50">
											Drag 'n' drop some files here, or click the button below to add manually.
										</p>
										<HasPermission permissions={[userPermissions.ADD_EVIDENCE]}>
											{metadata.evidence_type !== "Testimonial" ? (
												<Button disabled={removeFromList === true && metadata.parties === "Defendant"} className="mt-2" type="button" onClick={open} size="sm">
													<IconPlus size={14} stroke={2} />
													<span>Add</span>
												</Button>
											) : (
												<DropdownMenu modal={false}>
													<DropdownMenuTrigger asChild>
														<Button className="" size="sm" variant="default" type="button">
															<span className="">Add</span>
															<IconChevronDown size={16} stroke={2.5} />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="start">
														<DropdownMenuItem onClick={open}>
															<div className="flex flex-row items-center gap-2 px-2 py-1">
																<span>Witness with J.A.</span>
															</div>
														</DropdownMenuItem>
														<DropdownMenuItem
															onClick={() => {
																const noFile: AcceptedFile = {
																	_id: uuidv4(),
																	parent_id: null,
																	file: null,
																	preview: null,
																	_depth: 0,
																	type: null,
																	lastModified: 0,
																	size: 0,
																	stenography: null,
																	affidavit: null,
																};
																append(noFile);
																setAddNewEvidence(true);
															}}
														>
															<div className="flex flex-row items-center gap-2 px-2 py-1">
																<span>Witness w/o J.A.</span>
															</div>
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											)}
										</HasPermission>
									</div>
								</div>
							) : metadata.evidence_type !== "Testimonial" && !displayOnly ? (
								<div {...getRootProps()} className="dropzone inline-flex flex-col justify-center items-center w-full mb-2">
									<input {...getInputProps()} />
									<div className="flex flex-row items-center justify-between w-full gap-4">
										<span className="text-sm font-semibold text-foreground-dark">{metadata?.evidence_type} Evidence List</span>

										<HasPermission permissions={[userPermissions.ADD_EVIDENCE]}>
											<Button className="mt-2" type="button" disabled={removeFromList === true && metadata.parties === "Defendant"} onClick={open} size="sm">
												<IconPlus size={14} stroke={2} />
												<span>Add</span>
											</Button>
										</HasPermission>
									</div>
								</div>
							) : (
								!displayOnly && (
									<div className="flex flex-col justify-end items-center w-full mb-2">
										<input {...getInputProps()} />
										<div className="flex flex-row items-center justify-between w-full gap-4">
											<span className="text-sm font-semibold text-foreground-dark">{metadata?.evidence_type} Evidence List</span>
											<DropdownMenu modal={false}>
												<DropdownMenuTrigger asChild>
													<Button
														className="mt-2"
														size="sm"
														variant="default"
														type="button"
														disabled={removeFromList === true && metadata.parties === "Defendant"}
													>
														<span className="">Add</span>
														<IconChevronDown size={16} stroke={2.5} />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="start">
													<DropdownMenuItem onClick={open}>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span>Witness with J.A.</span>
														</div>
													</DropdownMenuItem>
													<DropdownMenuItem
														onClick={() => {
															const noFile: AcceptedFile = {
																_id: uuidv4(),
																parent_id: null,
																file: null,
																preview: null,
																_depth: 0,
																type: null,
																lastModified: 0,
																size: 0,
																stenography: null,
																affidavit: null,
															};
															append(noFile);
															setAddNewEvidence(true);
														}}
													>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span>Witness w/o J.A.</span>
														</div>
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</div>
									</div>
								)
							)}
							<DndContext collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
								<SortableContext items={fields.map((file) => file._id)} strategy={verticalListSortingStrategy}>
									<div className="flex flex-col w-full pl-3">
										<div className="flex flex-col w-full pl-8 border-l border-dashed border-border">
											{files
												.filter((f) => !f.parent_id)
												.map((file) => {
													const flatIndex = files.findIndex((f) => f._id === file._id);

													return (
														<EvidenceItem
															key={file._id}
															caseData={caseData}
															data={data}
															removeFromList={removeFromList}
															setIsEdit={setIsEditing}
															onEdit={isEditing}
															allFiles={files}
															index={flatIndex}
															file={file}
															form={form}
															schema={schema}
															isDragging={isDragging}
															metadata={metadata}
															files={files.filter((f) => f.parent_id === file._id)}
															onDelete={(row) => {
																handleRemoveRow(row);
															}}
															handleAmendDelete={handleAmendDelete}
															handleUpdateAffidavit={handleUpdateAffidavit}
															handleUpdateExhibit={handleUpdateExhibit}
															deletedData={deletedData}
															handleAddSubmarking={handleAddSubmarking}
															displayOnly={displayOnly}
														/>
													);
												})}
										</div>
									</div>
								</SortableContext>
							</DndContext>
						</div>
					</ScrollArea>
				</div>

				{(isAddingSubmarking && !displayOnly) ||
				(!onAdded && onSubmit && isEditing && !removeFromList && !displayOnly) ||
				(!onAdded && isNewEvidence && !removeFromList && !displayOnly) ? (
					<div {...getRootProps()} className="flex flex-row items-center justify-end gap-4 px-4 h-[50px] border-t border-border/50">
						<HasPermission permissions={[userPermissions.UPDATE_EVIDENCE]}>
							<div className="flex flex-row w-full items-center justify-between gap-4">
								{metadata.evidence_type !== "Testimonial" ? (
									<Button type="button" onClick={open} size="sm">
										<IconPlus size={14} stroke={2} />
										<span>Add</span>
										<input {...getInputProps()} />
									</Button>
								) : (
									<div className="flex flex-col justify-end items-center w-full mb-2">
										<input {...getInputProps()} />
										<div className="flex flex-row items-center justify-between w-full gap-4">
											<DropdownMenu modal={false}>
												<DropdownMenuTrigger asChild>
													<Button className="mt-2" size="sm" variant="default" type="button" disabled={metadata.parties === "Defendant" && removeFromList}>
														<span className="">Add</span>
														<IconChevronDown size={16} stroke={2.5} />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="start">
													<DropdownMenuItem onClick={open}>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span>Witness with J.A.</span>
														</div>
													</DropdownMenuItem>
													<DropdownMenuItem
														onClick={() => {
															const noFile: AcceptedFile = {
																_id: uuidv4(),
																parent_id: null,
																file: null,
																preview: null,
																_depth: 0,
																type: null,
																lastModified: 0,
																size: 0,
																stenography: null,
																affidavit: null,
															};
															append(noFile);
															setIsEditing(true);
														}}
													>
														<div className="flex flex-row items-center gap-2 px-2 py-1">
															<span>Witness w/o J.A.</span>
														</div>
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</div>
									</div>
								)}
								<div className="flex flex-row gap-2">
									<Button
										size="sm"
										type="button"
										variant="ghost"
										onClick={() => {
											setIsEditing(false);
											setAddingSubmarking(false);
											setAddNewEvidence(false);
											form.reset();
										}}
									>
										Cancel
									</Button>
									<Button size="sm" type="submit" variant="green" disabled={submitSuccess} loading={submitSuccess}>
										Save Changes
									</Button>
								</div>
							</div>
						</HasPermission>
					</div>
				) : (
					(!displayOnly && isAddingSubmarking) ||
					isNewEvidence ||
					(!onAdded && !removeFromList && !displayOnly) ||
					(!onAdded && onSubmit && !removeFromList && !displayOnly && (
						<div {...getRootProps()} className="flex flex-row items-center justify-end gap-4 px-4 h-[50px] border-t border-border/50">
							<HasPermission permissions={[userPermissions.UPDATE_EVIDENCE]}>
								<div className="flex flex-row w-full items-center justify-between gap-4">
									<Button type="button" onClick={open} size="sm">
										<IconPlus size={14} stroke={2} />
										<span>Add</span>
										<input {...getInputProps()} />
									</Button>
									<div className="flex flex-row gap-2">
										<Button
											size="sm"
											type="button"
											variant="ghost"
											onClick={() => {
												setIsEditing(false);
												setAddNewEvidence(false);
												setAddingSubmarking(false);
												form.reset();
											}}
										>
											Cancel
										</Button>
										<Button size="sm" type="submit" variant="green" disabled={submitSuccess} loading={submitSuccess}>
											Save Changes
										</Button>
									</div>
								</div>
							</HasPermission>
						</div>
					))
				)}
			</form>
		</Form>
	);
}
